import { useState } from "react";
import { ArrowUpRight, LayoutGrid, LifeBuoy, List as ListIcon, Loader2, Plus, Table as TableIcon, Clock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTenantStore } from "@/stores/useTenantStore";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import {
  useAssignTicket,
  useMyTickets,
  useSetTicketStatus,
  useTicketDetail,
  useTicketExperts,
  useTickets,
} from "@/hooks/useTickets";
import type { TicketStatus } from "@/services/ticketsService";
import { STATUSES, statusMeta } from "@/components/tickets/ticketConstants";
import { CategoryBadge } from "@/components/tickets/CategoryBadge";
import { useSupportCategories } from "@/hooks/useSupportCategories";
import { KanbanView } from "@/components/tickets/KanbanView";
import { ListView } from "@/components/tickets/ListView";
import { TicketsTableView } from "@/components/tickets/TicketsTableView";
import { NewTicketModal } from "@/components/tickets/NewTicketModal";
import { DepartmentsPanel } from "@/components/tickets/departments/DepartmentsPanel";

type ViewMode = "kanban" | "list" | "table";

const VIEW_BUTTONS: { key: ViewMode; label: string; icon: JSX.Element }[] = [
  { key: "kanban", label: "Kanban", icon: <LayoutGrid className="h-4 w-4" /> },
  { key: "list", label: "List", icon: <ListIcon className="h-4 w-4" /> },
  { key: "table", label: "Table", icon: <TableIcon className="h-4 w-4" /> },
];

export default function SupportTicketsBoardPage() {
  const { currentTenant } = useTenantStore();
  const userProfile = useWorkspaceStore((s) => s.userProfile);

  const platformRole = userProfile?.platform_role;
  const isElevated = platformRole === "super_admin" || platformRole === "platform_owner";
  const isExpert = platformRole === "expert";
  const isCrossTenant = isElevated || isExpert;
  // Staff-level board controls (drag-to-change-status, assignee editing) —
  // both elevated and department-scoped experts get the full toolset.
  const isStaff = isCrossTenant;

  const mine = useMyTickets(undefined, isCrossTenant);
  const tenantScoped = useTickets(isCrossTenant ? null : currentTenant);
  const { data, isLoading } = isCrossTenant ? mine : tenantScoped;

  const [openId, setOpenId] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("kanban");
  const [newTicketOpen, setNewTicketOpen] = useState(false);

  const tickets = data?.items ?? [];

  const headerCopy = isElevated
    ? "All client requests across every workspace — drag a card to change status."
    : isExpert
      ? userProfile?.department
        ? `Your assigned, requested, and ${userProfile.department} tickets — drag a card to change status.`
        : "Your assigned and requested tickets — drag a card to change status."
      : "Your support requests and their status.";

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <LifeBuoy className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Support Tickets</h1>
          <p className="text-sm text-muted-foreground">{headerCopy}</p>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-muted-foreground tabular-nums">{tickets.length} open items</span>
          <Button type="button" size="sm" onClick={() => setNewTicketOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Renders nothing unless super_admin / platform_owner. */}
      <DepartmentsPanel />

      <div className="mb-4 inline-flex items-center gap-1 rounded-lg bg-muted p-1">
        {VIEW_BUTTONS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => setView(v.key)}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors",
              view === v.key
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {v.icon}
            <span className="hidden sm:inline">{v.label}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tickets…
        </div>
      ) : view === "kanban" ? (
        <KanbanView tickets={tickets} isStaff={isStaff} isCrossTenant={isCrossTenant} onOpen={setOpenId} />
      ) : view === "list" ? (
        <ListView tickets={tickets} isCrossTenant={isCrossTenant} onOpen={setOpenId} />
      ) : (
        <TicketsTableView tickets={tickets} isCrossTenant={isCrossTenant} onOpen={setOpenId} />
      )}

      <NewTicketModal open={newTicketOpen} onClose={() => setNewTicketOpen(false)} />

      <TicketDetailSheet
        ticketId={openId}
        isStaff={isStaff}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}

function TicketDetailSheet({
  ticketId,
  isStaff,
  onClose,
}: {
  ticketId: string | null;
  isStaff: boolean;
  onClose: () => void;
}) {
  const { data: ticket } = useTicketDetail(ticketId);
  const setStatus = useSetTicketStatus();
  const assign = useAssignTicket();
  const { data: experts = [] } = useTicketExperts();
  const teamworkUrl = ticket?.details?.teamwork?.ticket_url;
  const { routingEnabled } = useSupportCategories();

  return (
    <Sheet open={Boolean(ticketId)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        {ticket ? (
          <>
            <SheetHeader>
              <SheetTitle className="text-left">{ticket.title}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-5 text-sm">
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-3">
                <CategoryBadge category={ticket.category} routingEnabled={routingEnabled} />
                <span className="text-muted-foreground">→ routed to</span>
                <span className="font-semibold text-foreground">{ticket.team}</span>
                {teamworkUrl ? (
                  <a
                    href={teamworkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-coral hover:underline"
                  >
                    View in Teamwork <ArrowUpRight className="h-3 w-3" />
                  </a>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</p>
                  {isStaff ? (
                    <Select
                      value={ticket.status}
                      onValueChange={(v) => setStatus.mutate({ id: ticket.id, status: v as TicketStatus })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold", statusMeta(ticket.status).style)}>
                      <span className={cn("h-1.5 w-1.5 rounded-full", statusMeta(ticket.status).dot)} />
                      {statusMeta(ticket.status).label}
                    </span>
                  )}
                </div>
                {isStaff ? (
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assignee</p>
                    <Select
                      value={ticket.assignee ?? undefined}
                      onValueChange={(v) => assign.mutate({ id: ticket.id, assignee: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        {experts.length === 0 ? (
                          <SelectItem value="__none" disabled>No experts configured</SelectItem>
                        ) : (
                          experts.map((e) => (
                            <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>

              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request</p>
                <div className="rounded-lg border border-border bg-muted/50 p-3 leading-relaxed text-foreground">
                  {ticket.description || "—"}
                </div>
              </div>

              <dl className="grid grid-cols-[88px_1fr] gap-x-3 gap-y-2 text-[13px]">
                <dt className="text-muted-foreground">Workspace</dt><dd>{ticket.tenant_id}</dd>
                <dt className="text-muted-foreground">Requested by</dt><dd>{ticket.requested_by || "—"}</dd>
              </dl>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Activity</p>
                <ul className="space-y-3">
                  {ticket.activity.map((a) => (
                    <li key={a.id} className="flex gap-2.5">
                      <span className="mt-1 h-2 w-2 flex-none rounded-full bg-coral" />
                      <div>
                        <p className="text-[13px] font-medium text-foreground">
                          {a.action === "status_changed"
                            ? `Status → ${statusMeta(a.new_status ?? "").label}`
                            : a.action === "created"
                              ? "Requested"
                              : a.detail || a.action}
                        </p>
                        <p className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {new Date(a.created_at).toLocaleString()}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
